// JOBE-0044.10: PeopleValuePopover — multi-select roster of humans and
// agents for the `owner` / `by` / `watcher` predicates. Reads the
// roster from a `roster` prop (parent supplies; v1 uses an empty
// default when not wired).

import { useEffect, useMemo, useRef, useState } from 'react';

export function PeopleValuePopover({
  roster,
  initialValues = [],
  onSubmit,
  onCancel,
}) {
  const humans = (roster && Array.isArray(roster.humans)) ? roster.humans : [];
  const agents = (roster && Array.isArray(roster.agents)) ? roster.agents : [];

  const [selected, setSelected] = useState(() => new Set(initialValues));
  const [query, setQuery] = useState('');
  const searchRef = useRef(null);

  useEffect(() => {
    searchRef.current?.focus();
  }, []);

  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      if (onCancel) onCancel();
    }
  };

  const filterRows = (rows) => {
    if (!query.trim()) return rows;
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      const hay = `${r.urn || ''} ${r.displayName || ''} ${r.name || ''}`.toLowerCase();
      return hay.includes(q);
    });
  };
  const filteredHumans = useMemo(() => filterRows(humans), [humans, query]);
  const filteredAgents = useMemo(() => filterRows(agents), [agents, query]);

  const toggle = (urn) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(urn)) next.delete(urn);
      else next.add(urn);
      return next;
    });
  };

  const submit = () => {
    if (selected.size === 0) return;
    if (onSubmit) onSubmit(Array.from(selected));
  };

  return (
    <div
      className="library-composer-popover library-composer-people"
      role="dialog"
      aria-label="Pick people"
      onKeyDown={onKeyDown}
    >
      <div className="library-composer-popover-head">
        <input
          ref={searchRef}
          type="text"
          className="library-composer-search"
          placeholder="Find a person…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          aria-label="Search people"
        />
      </div>
      <div className="library-composer-popover-body">
        <ul className="library-composer-roster-list" aria-label="Humans">
          {filteredHumans.length === 0 && (
            <li className="library-composer-empty">No humans</li>
          )}
          {filteredHumans.map((p) => (
            <li key={p.urn}>
              <label className="library-composer-row">
                <input
                  type="checkbox"
                  checked={selected.has(p.urn)}
                  onChange={() => toggle(p.urn)}
                />
                <span className="library-composer-row-avatar library-composer-row-avatar-human" aria-hidden="true" />
                <span className="library-composer-row-label">
                  {p.displayName || p.name || p.urn}
                </span>
              </label>
            </li>
          ))}
        </ul>
        <ul className="library-composer-roster-list" aria-label="Agents">
          {filteredAgents.length === 0 && (
            <li className="library-composer-empty">No agents</li>
          )}
          {filteredAgents.map((a) => (
            <li key={a.urn}>
              <label className="library-composer-row">
                <input
                  type="checkbox"
                  checked={selected.has(a.urn)}
                  onChange={() => toggle(a.urn)}
                />
                <span className="library-composer-row-avatar library-composer-row-avatar-agent" aria-hidden="true" />
                <span className="library-composer-row-label">
                  {a.displayName || a.name || a.urn}
                </span>
                <span className="library-composer-row-tag">AGENT</span>
              </label>
            </li>
          ))}
        </ul>
      </div>
      <div className="library-composer-popover-foot">
        <button
          type="button"
          className="library-composer-submit"
          onClick={submit}
          disabled={selected.size === 0}
        >
          Add chip
        </button>
      </div>
    </div>
  );
}
